import { HttpEventType } from '@angular/common/http';
import { Injectable, computed, effect, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FuseNavigationService } from '../../../@fuse/components/navigation/navigation.service';
import { SessionEventsService } from '../../gt2/api/session-events.service';
import { ChatApiService } from '../../gt2/api/chat-api.service';
import { GuidGenerator } from '../../gt2/utils/guid-generator';
import { isImageFile } from '../../main/apps/chat/chat-upload.util';
import {
  ChatAttachment,
  ChatConversation,
  ChatConversationTouchedEvent,
  ChatMessage,
  ChatMessageDeletedEvent,
  ChatParticipantsChangedEvent,
  ChatReaction,
  ChatReactionAddedEvent,
  ChatReactionRemovedEvent,
  ChatUserSummary,
} from '../../main/apps/chat/models/chat.models';
import { ChatSocketService } from './chat-socket.service';

/**
 * Source de vérité UNIQUE du chat (signals). Messages en ASC par conversation.
 * Envoi optimiste : message `pending` avec client_id local, réconcilié sur le
 * 201 (pas d'écho socket de ses propres messages — toOthers côté backend).
 * Gap-fill à la reconnexion : re-fetch page 1 + merge dédupliqué par uuid, et
 * rechargement de la liste (les unread counts serveur sont la vérité).
 */
@Injectable({ providedIn: 'root' })
export class ChatStore {
  private readonly conversationsMap = signal<Map<string, ChatConversation>>(new Map());
  private readonly messagesMap = signal<Map<string, ChatMessage[]>>(new Map());
  private readonly pagination = new Map<string, { hasMore: boolean; oldestCursor: string | null }>();

  public readonly activeConversationId = signal<string | null>(null);
  public readonly loadingConversations = signal(false);
  public readonly loadingOlder = signal(false);

  /** Uuids des users en ligne (channel presence tenant-wide, T6). */
  public readonly onlineUserIds = signal<Set<string>>(new Set());

  public readonly sortedConversations = computed(() => {
    return [...this.conversationsMap().values()].sort((a, b) => {
      const ta = a.last_message_at ?? a.created_at ?? '';
      const tb = b.last_message_at ?? b.created_at ?? '';
      return tb.localeCompare(ta);
    });
  });

  /** Badge global — exclut les conversations mutées et archivées (T6). */
  public readonly totalUnread = computed(() => {
    let total = 0;
    for (const conversation of this.conversationsMap().values()) {
      if (conversation.is_muted || conversation.is_archived) {
        continue;
      }
      total += conversation.unread_count ?? 0;
    }
    return total;
  });

  /** Mentions non lues TOUTES conversations (muted INCLUS — la mention perce le mute, T7). */
  public readonly totalMentions = computed(() => {
    let total = 0;
    for (const conversation of this.conversationsMap().values()) {
      total += conversation.unread_mentions_count ?? 0;
    }
    return total;
  });

  /** Messages épinglés de la conversation active (panneau T6). */
  public readonly pinnedMessages = signal<ChatMessage[]>([]);

  /** Position du séparateur « Nouveaux messages » à l'ouverture (T6). */
  public readonly firstUnreadUuid = signal<string | null>(null);

  public readonly activeConversation = computed(() => {
    const id = this.activeConversationId();
    return id ? (this.conversationsMap().get(id) ?? null) : null;
  });

  public readonly activeMessages = computed(() => {
    const id = this.activeConversationId();
    return id ? (this.messagesMap().get(id) ?? []) : [];
  });

  private me: ChatUserSummary | null = null;
  private initializedForUuid: string | null = null;

  // ---- Mark-read intelligent (T6) : actif + bas du fil + onglet visible ----
  private viewerAtBottom = true;
  private markReadTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MARK_READ_DEBOUNCE_MS = 500;

  constructor(
    private api: ChatApiService,
    private socket: ChatSocketService,
    private sessionEvents: SessionEventsService,
    private fuseNavigationService: FuseNavigationService,
  ) {
    // Badge global dans la navigation (premier usage dynamique du badge Fuse).
    // Mentions : « @ » suffixe le compteur, ou pastille « @ » seule si tout
    // l'unread restant est muté (la mention perce le mute, T7).
    effect(() => {
      const total = this.totalUnread();
      const mentions = this.totalMentions();
      let title: string | null = null;
      if (total > 0) {
        title = mentions > 0 ? `${total} @` : String(total);
      } else if (mentions > 0) {
        title = '@';
      }
      this.fuseNavigationService.updateNavigationItem('godChat', {
        badge: title !== null ? { title, bg: '#F44336', fg: '#FFFFFF' } : (null as unknown as undefined),
      });
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const active = this.activeConversationId();
          if (active) {
            this.requestMarkRead(active);
          }
        }
      });
    }
    this.socket.messageSent$.subscribe((m) => this.onMessageSent(m));
    this.socket.messageUpdated$.subscribe((m) => this.replaceMessage(m));
    this.socket.messagePinned$.subscribe((m) => {
      this.replaceMessage(m);
      this.onPinnedChanged(m, true);
    });
    this.socket.messageUnpinned$.subscribe((m) => {
      this.replaceMessage(m);
      this.onPinnedChanged(m, false);
    });
    this.socket.messageDeleted$.subscribe((e) => this.onMessageDeleted(e));
    this.socket.reactionAdded$.subscribe((e) => this.onReactionAdded(e));
    this.socket.reactionRemoved$.subscribe((e) => this.onReactionRemoved(e));
    this.socket.conversationTouched$.subscribe((e) => this.onConversationTouched(e));
    this.socket.conversationUpdated$.subscribe((c) => this.onConversationUpdated(c));
    this.socket.conversationCreated$.subscribe((c) => {
      this.upsertConversation(c);
      this.hydrateIfIncomplete(c);
    });
    this.socket.participantsChanged$.subscribe((e) => this.onParticipantsChanged(e));
    this.socket.onlineHere$.subscribe((members) => this.onlineUserIds.set(new Set(members.map((m) => m.uuid ?? m.id))));
    this.socket.onlineJoining$.subscribe((member) =>
      this.onlineUserIds.update((set) => new Set(set).add(member.uuid ?? member.id)),
    );
    this.socket.onlineLeaving$.subscribe((member) =>
      this.onlineUserIds.update((set) => {
        const next = new Set(set);
        next.delete(member.uuid ?? member.id);
        return next;
      }),
    );
    this.socket.reconnected$.subscribe(() => this.onReconnected());
    this.sessionEvents.logout$.subscribe(() => this.reset());
  }

  // ---- Cycle de vie ----

  /**
   * Appelé au bootstrap global (badge nav) ET à l'entrée du module chat —
   * idempotent par user (le module réutilise le store déjà initialisé).
   */
  public init(me: ChatUserSummary): void {
    if (this.initializedForUuid === me.uuid) {
      return;
    }
    this.initializedForUuid = me.uuid;
    this.me = me;
    this.socket.init(me.uuid);
    this.loadConversations();
  }

  public loadConversations(): void {
    this.loadingConversations.set(true);
    this.api.getConversations().subscribe({
      next: (conversations) => {
        this.conversationsMap.update((map) => {
          const next = new Map(map);
          for (const conversation of conversations) {
            next.set(conversation.uuid, { ...map.get(conversation.uuid), ...conversation });
          }
          return next;
        });
        this.loadingConversations.set(false);
      },
      error: () => this.loadingConversations.set(false),
    });
  }

  public openConversation(uuid: string): void {
    const previous = this.activeConversationId();
    if (previous === uuid) {
      return;
    }
    if (previous) {
      this.socket.unsubscribeConversation(previous);
    }

    this.activeConversationId.set(uuid);
    this.socket.subscribeConversation(uuid);
    this.pinnedMessages.set([]);

    if (!this.messagesMap().has(uuid)) {
      this.loadFirstPage(uuid);
    } else {
      this.computeFirstUnread(uuid);
      this.requestMarkRead(uuid);
    }
  }

  public closeConversation(): void {
    const current = this.activeConversationId();
    if (current) {
      this.socket.unsubscribeConversation(current);
      this.activeConversationId.set(null);
      this.firstUnreadUuid.set(null);
      this.pinnedMessages.set([]);
    }
  }

  /** Séparateur « Nouveaux messages » : position du last_read approximée
   *  depuis unread_count sur la page 1 (les 50 plus récents). */
  private computeFirstUnread(uuid: string): void {
    const unread = this.conversationsMap().get(uuid)?.unread_count ?? 0;
    const messages = this.messagesMap().get(uuid) ?? [];
    if (unread > 0 && messages.length >= unread) {
      this.firstUnreadUuid.set(messages[messages.length - unread]?.uuid ?? null);
    } else {
      this.firstUnreadUuid.set(null);
    }
  }

  private reset(): void {
    this.conversationsMap.set(new Map());
    this.messagesMap.set(new Map());
    this.pagination.clear();
    this.activeConversationId.set(null);
    this.onlineUserIds.set(new Set());
    this.pinnedMessages.set([]);
    this.firstUnreadUuid.set(null);
    this.me = null;
    this.initializedForUuid = null;
  }

  /** L'interlocuteur d'un DM est-il en ligne ? (participants chargés requis) */
  public isDirectPeerOnline(conversation: ChatConversation): boolean {
    if (conversation.type !== 'direct') {
      return false;
    }
    const peer = (conversation.participants ?? []).find((p) => p.user && p.user.uuid !== this.me?.uuid);
    return peer?.user ? this.onlineUserIds().has(peer.user.uuid) : false;
  }

  // ---- Messages : chargement & pagination cursor ----

  private loadFirstPage(uuid: string): void {
    this.api.getMessages(uuid).subscribe((page) => {
      const now = Date.now();
      const ascending = [...page.items].reverse().map((m) => ({ ...m, fetched_at: now }));
      this.messagesMap.update((map) => new Map(map).set(uuid, ascending));
      this.pagination.set(uuid, { hasMore: page.has_more, oldestCursor: page.next_cursor });
      this.computeFirstUnread(uuid);
      this.requestMarkRead(uuid);
    });
  }

  public hasMore(uuid: string): boolean {
    return this.pagination.get(uuid)?.hasMore ?? false;
  }

  /** Prepend d'une page plus ancienne — résout avec le nombre de messages ajoutés. */
  public async loadOlder(uuid: string): Promise<number> {
    const state = this.pagination.get(uuid);
    if (!state?.hasMore || !state.oldestCursor || this.loadingOlder()) {
      return 0;
    }

    this.loadingOlder.set(true);
    try {
      const page = await firstValueFrom(this.api.getMessages(uuid, { before: state.oldestCursor }));
      const existing = this.messagesMap().get(uuid) ?? [];
      const known = new Set(existing.map((m) => m.uuid));
      const now = Date.now();
      const older = [...page.items]
        .reverse()
        .filter((m) => !known.has(m.uuid))
        .map((m) => ({ ...m, fetched_at: now }));

      this.messagesMap.update((map) => new Map(map).set(uuid, [...older, ...existing]));
      this.pagination.set(uuid, { hasMore: page.has_more, oldestCursor: page.next_cursor });

      return older.length;
    } finally {
      this.loadingOlder.set(false);
    }
  }

  // ---- Envoi optimiste ----

  public sendMessage(conversationUuid: string, body: string, parentUuid: string | null = null): void {
    const clientId = GuidGenerator.generate();
    const parent = parentUuid
      ? ((this.messagesMap().get(conversationUuid) ?? []).find((m) => m.uuid === parentUuid) ?? null)
      : null;

    const optimistic: ChatMessage = {
      object: 'Message',
      uuid: clientId, // provisoire — remplacé par l'uuid serveur au 201
      conversation_uuid: conversationUuid,
      type: 'text',
      body,
      metadata: null,
      sender: this.me,
      parent: parent ? { uuid: parent.uuid, body: parent.body, sender: parent.sender, deleted: parent.deleted } : null,
      reactions: [],
      mentions: [],
      mentions_all: false,
      attachments: [],
      is_edited: false,
      is_pinned: false,
      deleted: false,
      created_at: new Date().toISOString(),
      pending: true,
      client_id: clientId,
    };

    this.appendMessage(optimistic);
    this.touchConversationLocally(conversationUuid, optimistic);

    this.api.sendMessage(conversationUuid, { body, parent_uuid: parentUuid }).subscribe({
      next: (server) => this.reconcileOptimistic(conversationUuid, clientId, server),
      error: () => this.markOptimisticError(conversationUuid, clientId),
    });
  }

  public retryMessage(conversationUuid: string, clientId: string): void {
    // Envoi multipart échoué : les File[] sont retenus, on relance l'upload.
    if (this.pendingUploads.has(clientId)) {
      this.updateMessageInPlace(conversationUuid, clientId, (m) => ({ ...m, pending: true, error: false, upload_progress: 0 }));
      this.performUpload(conversationUuid, clientId);
      return;
    }

    const failed = (this.messagesMap().get(conversationUuid) ?? []).find((m) => m.client_id === clientId);
    if (!failed || !failed.body) {
      return;
    }

    this.updateMessageInPlace(conversationUuid, clientId, (m) => ({ ...m, pending: true, error: false }));
    this.api.sendMessage(conversationUuid, { body: failed.body, parent_uuid: failed.parent?.uuid ?? null }).subscribe({
      next: (server) => this.reconcileOptimistic(conversationUuid, clientId, server),
      error: () => this.markOptimisticError(conversationUuid, clientId),
    });
  }

  // ---- Envoi avec fichiers (T6) ----

  private readonly pendingUploads = new Map<
    string,
    { body: string | null; files: File[]; parentUuid: string | null; objectUrls: string[] }
  >();

  public sendMessageWithFiles(
    conversationUuid: string,
    body: string | null,
    files: File[],
    parentUuid: string | null = null,
  ): void {
    if (files.length === 0) {
      if (body) {
        this.sendMessage(conversationUuid, body, parentUuid);
      }
      return;
    }

    const clientId = GuidGenerator.generate();
    const objectUrls: string[] = [];
    const attachments: ChatAttachment[] = files.map((file, index) => {
      const objectUrl = URL.createObjectURL(file);
      objectUrls.push(objectUrl);
      const isImage = isImageFile(file);

      return {
        uuid: `local-${clientId}-${index}`,
        file_name: file.name,
        mime_type: file.type,
        size: file.size,
        url: objectUrl,
        thumb_url: isImage ? objectUrl : null,
        width: null,
        height: null,
      };
    });

    const optimistic: ChatMessage = {
      object: 'Message',
      uuid: clientId,
      conversation_uuid: conversationUuid,
      type: files.every(isImageFile) ? 'image' : 'file',
      body,
      metadata: null,
      sender: this.me,
      parent: null,
      reactions: [],
      mentions: [],
      mentions_all: false,
      attachments,
      is_edited: false,
      is_pinned: false,
      deleted: false,
      created_at: new Date().toISOString(),
      pending: true,
      client_id: clientId,
      upload_progress: 0,
    };

    this.appendMessage(optimistic);
    this.touchConversationLocally(conversationUuid, optimistic);
    this.pendingUploads.set(clientId, { body, files, parentUuid, objectUrls });
    this.performUpload(conversationUuid, clientId);
  }

  private performUpload(conversationUuid: string, clientId: string): void {
    const pending = this.pendingUploads.get(clientId);
    if (!pending) {
      return;
    }

    this.api
      .sendMessageWithProgress(conversationUuid, {
        body: pending.body,
        parent_uuid: pending.parentUuid,
        files: pending.files,
      })
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((100 * event.loaded) / event.total);
            this.updateMessageInPlace(conversationUuid, clientId, (m) => ({ ...m, upload_progress: percent }));
          } else if (event.type === HttpEventType.Response && event.body) {
            pending.objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
            this.pendingUploads.delete(clientId);
            this.reconcileOptimistic(conversationUuid, clientId, event.body.data);
          }
        },
        error: () => this.markOptimisticError(conversationUuid, clientId),
      });
  }

  /** Message re-fetché (URLs signées fraîches — TTL 60 min des attachments). */
  public async refreshMessage(messageUuid: string): Promise<ChatMessage> {
    const fresh = await firstValueFrom(this.api.getMessage(messageUuid));
    this.replaceMessage(fresh);
    return fresh;
  }

  private reconcileOptimistic(conversationUuid: string, clientId: string, server: ChatMessage): void {
    this.updateMessages(conversationUuid, (messages) =>
      messages.map((m) => (m.client_id === clientId ? { ...server, client_id: clientId } : m)),
    );
    this.touchConversationLocally(conversationUuid, server);
    this.requestMarkRead(conversationUuid);
  }

  private markOptimisticError(conversationUuid: string, clientId: string): void {
    this.updateMessageInPlace(conversationUuid, clientId, (m) => ({ ...m, pending: false, error: true }));
  }

  // ---- Mutations messages (REST) ----

  public editMessage(message: ChatMessage, body: string): void {
    this.api.editMessage(message.uuid, body).subscribe((updated) => this.replaceMessage(updated));
  }

  public deleteMessage(message: ChatMessage): void {
    this.api.deleteMessage(message.uuid).subscribe((deleted) => this.replaceMessage(deleted));
  }

  public togglePin(message: ChatMessage): void {
    const call = message.is_pinned ? this.api.unpinMessage(message.uuid) : this.api.pinMessage(message.uuid);
    call.subscribe((updated) => {
      this.replaceMessage(updated);
      this.onPinnedChanged(updated, updated.is_pinned);
    });
  }

  /** Panneau des épinglés (T6). */
  public loadPinned(conversationUuid: string): void {
    this.api.getPinnedMessages(conversationUuid).subscribe((messages) => {
      if (this.activeConversationId() === conversationUuid) {
        this.pinnedMessages.set(messages);
      }
    });
  }

  private onPinnedChanged(message: ChatMessage, pinned: boolean): void {
    if (this.activeConversationId() !== message.conversation_uuid) {
      return;
    }
    this.pinnedMessages.update((list) => {
      const without = list.filter((m) => m.uuid !== message.uuid);
      return pinned ? [message, ...without] : without;
    });
  }

  /**
   * Toggle OPTIMISTE d'une réaction (T6) : mutation locale immédiate (les
   * handlers d'events servent de mutateurs), rollback si l'API échoue. La
   * réponse du POST (message complet) reste la réconciliation serveur.
   */
  public toggleReaction(message: ChatMessage, emoji: string): void {
    const meUuid = this.me?.uuid;
    if (!meUuid) {
      return;
    }

    const addEvent: ChatReactionAddedEvent = {
      message_uuid: message.uuid,
      conversation_uuid: message.conversation_uuid,
      emoji,
      reactor: this.me,
    };
    const removeEvent: ChatReactionRemovedEvent = {
      message_uuid: message.uuid,
      conversation_uuid: message.conversation_uuid,
      emoji,
      reactor_uuid: meUuid,
    };

    const hasReacted = message.reactions.find((r) => r.emoji === emoji)?.reacted ?? false;

    if (hasReacted) {
      this.onReactionRemoved(removeEvent);
      this.api.removeReaction(message.uuid, emoji).subscribe({
        error: () => this.onReactionAdded(addEvent), // rollback
      });
    } else {
      this.onReactionAdded(addEvent);
      this.api.addReaction(message.uuid, emoji).subscribe({
        next: (server) => this.replaceMessage(server),
        error: () => this.onReactionRemoved(removeEvent), // rollback
      });
    }
  }

  // ---- Création de conversations ----

  public createDirect(userUuid: string): void {
    this.api.createConversation({ type: 'direct', participant_uuid: userUuid }).subscribe(({ conversation }) => {
      this.upsertConversation(conversation);
      this.hydrateIfIncomplete(conversation);
      this.openConversation(conversation.uuid);
    });
  }

  public createGroup(name: string, participantUuids: string[]): void {
    this.api
      .createConversation({ type: 'group', name, participant_uuids: participantUuids })
      .subscribe(({ conversation }) => {
        this.upsertConversation(conversation);
        this.hydrateIfIncomplete(conversation);
        this.openConversation(conversation.uuid);
      });
  }

  /** Les payloads produits SANS contexte viewer (réponse du POST create,
   *  broadcast conversation.created) peuvent arriver avec display_name null —
   *  la ligne s'affiche alors sans nom ni initiales jusqu'au prochain refresh.
   *  On recharge la conversation complète (GET single = contexte viewer). */
  private hydrateIfIncomplete(conversation: ChatConversation): void {
    if (conversation.display_name || conversation.name) {
      return;
    }
    this.api.getConversation(conversation.uuid).subscribe({
      next: (full) => this.upsertConversation(full),
      error: () => {},
    });
  }

  public toggleMute(conversation: ChatConversation): void {
    const call = conversation.is_muted ? this.api.unmute(conversation.uuid) : this.api.mute(conversation.uuid);
    call.subscribe(() => {
      this.updateConversation(conversation.uuid, (c) => ({ ...c, is_muted: !conversation.is_muted }));
    });
  }

  // ---- Read (intelligent T6 : actif + bas du fil + onglet visible + debounce) ----

  public setViewerAtBottom(atBottom: boolean): void {
    const changed = this.viewerAtBottom !== atBottom;
    this.viewerAtBottom = atBottom;
    const active = this.activeConversationId();
    if (changed && atBottom && active) {
      this.requestMarkRead(active);
    }
  }

  private canMarkRead(conversationUuid: string): boolean {
    return (
      this.activeConversationId() === conversationUuid &&
      this.viewerAtBottom &&
      (typeof document === 'undefined' || document.visibilityState === 'visible')
    );
  }

  private requestMarkRead(conversationUuid: string): void {
    if (!this.canMarkRead(conversationUuid)) {
      return; // un message reçu onglet caché / hors bas de fil reste non lu
    }
    if (this.markReadTimer) {
      clearTimeout(this.markReadTimer);
    }
    this.markReadTimer = setTimeout(() => {
      if (this.canMarkRead(conversationUuid)) {
        this.markLatestRead(conversationUuid);
      }
    }, ChatStore.MARK_READ_DEBOUNCE_MS);
  }

  private markLatestRead(conversationUuid: string): void {
    const messages = this.messagesMap().get(conversationUuid) ?? [];
    const latest = [...messages].reverse().find((m) => !m.pending && !m.error);

    this.updateConversation(conversationUuid, (c) => ({ ...c, unread_count: 0, unread_mentions_count: 0 }));
    if (latest) {
      this.api.markRead(conversationUuid, latest.uuid).subscribe();
    }
  }

  // ---- Handlers socket ----

  private onMessageSent(message: ChatMessage): void {
    if (!this.messagesMap().has(message.conversation_uuid)) {
      return; // la liste est mise à jour via conversation.touched
    }
    this.appendMessage(message);
    this.touchConversationLocally(message.conversation_uuid, message);

    if (this.canMarkRead(message.conversation_uuid)) {
      this.requestMarkRead(message.conversation_uuid);
    } else {
      // Onglet caché ou hors bas de fil : le message reste non lu.
      this.updateConversation(message.conversation_uuid, (c) => ({
        ...c,
        unread_count: (c.unread_count ?? 0) + 1,
      }));
    }
  }

  private onConversationTouched(event: ChatConversationTouchedEvent): void {
    const existing = this.conversationsMap().get(event.conversation_uuid);
    if (!existing) {
      this.loadConversations(); // conversation inconnue localement — resync
      return;
    }

    this.updateConversation(event.conversation_uuid, (c) => ({
      ...c,
      last_message_at: event.sent_at ?? c.last_message_at,
      last_message: c.last_message
        ? { ...c.last_message, uuid: event.last_message.uuid, type: event.last_message.type, body: event.last_message.body, deleted: false }
        : c.last_message,
      // Le fil actif chargé gère son propre compteur via onMessageSent —
      // ici on incrémente seulement les conversations non chargées/inactive.
      unread_count: this.messagesMap().has(event.conversation_uuid)
        ? c.unread_count
        : (c.unread_count ?? 0) + 1,
      // Mention pour MOI (split backend T7) — sauf si je peux marquer lu.
      unread_mentions_count:
        event.has_mention && !this.canMarkRead(event.conversation_uuid)
          ? (c.unread_mentions_count ?? 0) + 1
          : c.unread_mentions_count,
    }));
  }

  private onMessageDeleted(event: ChatMessageDeletedEvent): void {
    this.updateMessageInPlace(event.conversation_uuid, event.uuid, (m) => ({
      ...m,
      deleted: true,
      body: null,
      metadata: null,
      reactions: [],
      mentions: [],
      mentions_all: false,
      attachments: [],
    }));
  }

  private onReactionAdded(event: ChatReactionAddedEvent): void {
    this.updateMessageInPlace(event.conversation_uuid, event.message_uuid, (m) => {
      const reactions: ChatReaction[] = [...m.reactions];
      const index = reactions.findIndex((r) => r.emoji === event.emoji);
      const reactorUuid = event.reactor?.uuid ?? null;
      const isMe = reactorUuid !== null && reactorUuid === this.me?.uuid;

      if (index >= 0) {
        const current = reactions[index];
        if (reactorUuid && current.reactors.includes(reactorUuid)) {
          return m; // déjà compté (écho improbable, idempotence)
        }
        reactions[index] = {
          ...current,
          count: current.count + 1,
          reacted: current.reacted || isMe,
          reactors: reactorUuid ? [...current.reactors, reactorUuid].slice(0, 10) : current.reactors,
        };
      } else {
        reactions.push({ emoji: event.emoji, count: 1, reacted: isMe, reactors: reactorUuid ? [reactorUuid] : [] });
      }

      return { ...m, reactions };
    });
  }

  private onReactionRemoved(event: ChatReactionRemovedEvent): void {
    this.updateMessageInPlace(event.conversation_uuid, event.message_uuid, (m) => {
      const reactions = m.reactions
        .map((r) => {
          if (r.emoji !== event.emoji) {
            return r;
          }
          return {
            ...r,
            count: r.count - 1,
            reacted: event.reactor_uuid === this.me?.uuid ? false : r.reacted,
            reactors: r.reactors.filter((uuid) => uuid !== event.reactor_uuid),
          };
        })
        .filter((r) => r.count > 0);

      return { ...m, reactions };
    });
  }

  private onConversationUpdated(incoming: ChatConversation): void {
    this.updateConversation(incoming.uuid, (existing) => ({
      ...existing,
      ...incoming,
      // Le payload broadcast est sans contexte requérant — préserver le local.
      unread_count: incoming.unread_count ?? existing.unread_count,
      my_role: incoming.my_role ?? existing.my_role,
      is_muted: incoming.is_muted ?? existing.is_muted,
      last_message: incoming.last_message ?? existing.last_message,
    }));
  }

  private onParticipantsChanged(event: ChatParticipantsChangedEvent): void {
    const meRemoved =
      (event.action === 'removed' || event.action === 'left') &&
      event.participants.some((p) => p.user?.uuid === this.me?.uuid);

    if (!meRemoved) {
      return;
    }

    this.socket.unsubscribeConversation(event.conversation_uuid);
    this.conversationsMap.update((map) => {
      const next = new Map(map);
      next.delete(event.conversation_uuid);
      return next;
    });
    this.messagesMap.update((map) => {
      const next = new Map(map);
      next.delete(event.conversation_uuid);
      return next;
    });
    this.pagination.delete(event.conversation_uuid);
    if (this.activeConversationId() === event.conversation_uuid) {
      this.activeConversationId.set(null);
    }
  }

  /** Gap-fill : la reconnexion a pu perdre des events — resync serveur. */
  private onReconnected(): void {
    this.loadConversations();

    const active = this.activeConversationId();
    if (!active || !this.messagesMap().has(active)) {
      return;
    }

    this.api.getMessages(active).subscribe((page) => {
      const incoming = [...page.items].reverse();
      const existing = this.messagesMap().get(active) ?? [];
      const incomingByUuid = new Map(incoming.map((m) => [m.uuid, m]));

      // Merge dédupliqué : maj des connus, ajout des manquants (les plus récents).
      const merged = existing.map((m) => incomingByUuid.get(m.uuid) ?? m);
      const knownUuids = new Set(existing.map((m) => m.uuid));
      for (const message of incoming) {
        if (!knownUuids.has(message.uuid)) {
          merged.push(message);
        }
      }

      this.messagesMap.update((map) => new Map(map).set(active, merged));
      this.requestMarkRead(active);
    });
  }

  // ---- Helpers immutables (signals) ----

  private upsertConversation(conversation: ChatConversation): void {
    this.conversationsMap.update((map) => {
      const existing = map.get(conversation.uuid);
      const merged: ChatConversation = { ...existing, ...conversation };
      // Un payload sans contexte viewer (POST create, broadcasts) porte des
      // champs d'affichage à null — ne jamais écraser une valeur déjà connue.
      if (existing) {
        if (conversation.display_name == null && existing.display_name != null) {
          merged.display_name = existing.display_name;
        }
        if (conversation.display_avatar == null && existing.display_avatar != null) {
          merged.display_avatar = existing.display_avatar;
        }
        if (conversation.participants == null && existing.participants != null) {
          merged.participants = existing.participants;
        }
      }
      const next = new Map(map);
      next.set(conversation.uuid, merged);
      return next;
    });
  }

  private updateConversation(uuid: string, mutate: (c: ChatConversation) => ChatConversation): void {
    const existing = this.conversationsMap().get(uuid);
    if (!existing) {
      return;
    }
    this.conversationsMap.update((map) => new Map(map).set(uuid, mutate(existing)));
  }

  private updateMessages(uuid: string, mutate: (messages: ChatMessage[]) => ChatMessage[]): void {
    const existing = this.messagesMap().get(uuid);
    if (!existing) {
      return;
    }
    this.messagesMap.update((map) => new Map(map).set(uuid, mutate(existing)));
  }

  private updateMessageInPlace(
    conversationUuid: string,
    messageUuidOrClientId: string,
    mutate: (m: ChatMessage) => ChatMessage,
  ): void {
    this.updateMessages(conversationUuid, (messages) =>
      messages.map((m) => (m.uuid === messageUuidOrClientId || m.client_id === messageUuidOrClientId ? mutate(m) : m)),
    );
  }

  private appendMessage(message: ChatMessage): void {
    this.updateMessages(message.conversation_uuid, (messages) => {
      if (messages.some((m) => m.uuid === message.uuid)) {
        return messages; // dédup (gap-fill / double événement)
      }
      return [...messages, { ...message, fetched_at: message.fetched_at ?? Date.now() }];
    });
  }

  private replaceMessage(message: ChatMessage): void {
    this.updateMessageInPlace(message.conversation_uuid, message.uuid, (m) => ({
      ...message,
      // Certaines réponses (réaction, pin) arrivent sans les pièces jointes —
      // ne jamais perdre celles qu'on a déjà (aucun flux ne les retire).
      attachments: message.attachments?.length ? message.attachments : m.attachments,
      client_id: m.client_id,
      fetched_at: Date.now(),
    }));
  }

  private touchConversationLocally(conversationUuid: string, message: ChatMessage): void {
    this.updateConversation(conversationUuid, (c) => ({
      ...c,
      last_message_at: message.created_at ?? c.last_message_at,
      last_message: message,
    }));
  }
}
